import { useAddWorkerListStore } from '@/store/addWorkerList';
import { useSelectedWorkerListStore } from '@/store/selectedWorkerList';
import useSubscribeStore from '@/store/subscribeMode';
import usePaymentInfo from '@/hooks/usePaymentInfo';
import type { WorkerPaymentInfo } from '@/types/company.types';

interface Props {
  rowData: WorkerPaymentInfo;
  isTrial: 1 | 0;
}

const WorkerManageCheckBox = ({ rowData, isTrial }: Props) => {
  const { id, name, isActive, restoreType } = rowData;
  const {
    selectedAddWorkerList,
    toggleAddWorker,
    selectedRestoreWorkerList,
    toggleRestoreWorker,
    selectedUnsubscribeWorkerList,
    toggleUnsubscribeWorker,
    selectedUndoUnsubscribeWorkerList,
    toggleUndoUnsubscribeWorker,
  } = useSelectedWorkerListStore();

  const { findAddWorkerByName } = useAddWorkerListStore();
  const { isSubscribeMode } = useSubscribeStore();

  // Get restoreType of already selected restore workers
  const { data: paymentData } = usePaymentInfo();
  const allWorkerList = [
    ...(paymentData?.data.workerList || []),
    ...(paymentData?.data.deletedWorkerList || []),
  ];
  
  const selectedRestoreWorkers = selectedRestoreWorkerList
    .map((workerId) => allWorkerList.find((w) => w.id === workerId))
    .filter(Boolean);
  
  const selectedRestoreTypes = selectedRestoreWorkers
    .map((w) => w?.restoreType)
    .filter((type): type is 'free' | 'paid' => type !== undefined);
  
  const hasFreeRestoreSelected = selectedRestoreTypes.includes('free');
  const hasPaidRestoreSelected = selectedRestoreTypes.includes('paid');
  
  // Check if this worker should be disabled based on restoreType conflict
  // Only applies to restore workers (비활성 or 활성 with restoreType)
  const isRestoreTypeConflict = isSubscribeMode && 
    restoreType && 
    ((hasFreeRestoreSelected && restoreType === 'paid') || 
     (hasPaidRestoreSelected && restoreType === 'free'));

  const isChecked =
    selectedRestoreWorkerList.includes(id) ||
    selectedAddWorkerList.some(
      (selectedAddWorker) => selectedAddWorker.name === name
    ) ||
    selectedUnsubscribeWorkerList.includes(id) ||
    selectedUndoUnsubscribeWorkerList.includes(id);

  // Count active workers (isActive === '활성') - workers with auto_renew = true
  const activeWorkersCount = (paymentData?.data.workerList || []).filter(
    (w) => w.isActive === '활성'
  ).length;

  // Check if this is the last active worker
  const isLastActiveWorker = isActive === '활성' && activeWorkersCount === 1;

  const selectWorkerHandler = () => {
    if (isActive === '활성' && !isSubscribeMode) {
      // Normal active worker (not in subscribe mode)
      toggleUnsubscribeWorker(id);
    } else if (isActive === '복구 가능') {
      // Free restore worker (can restore for free)
      toggleRestoreWorker(id);
    } else if (isActive === '비활성') {
      // Paid restore worker or normal inactive worker
      toggleRestoreWorker(id);
    } else if (isActive === '비활성 예정') {
      toggleUndoUnsubscribeWorker(id);
    } else if (isActive === '생성') {
      const addWorker = findAddWorkerByName(name);
      if (addWorker) {
        const { id, ...addWorkerWithoutId } = addWorker;
        toggleAddWorker(addWorkerWithoutId, isTrial);
      }
    }
  };

  return (
    <input
      type='checkbox'
      checked={isChecked}
      onChange={selectWorkerHandler}
          disabled={
            isRestoreTypeConflict ||
            isLastActiveWorker || // Disable if this is the last active worker
            (isSubscribeMode
              ? !(isActive === '생성' || isActive === '비활성' || isActive === '복구 가능')
              : (isActive !== '비활성 예정' ||
                  selectedUnsubscribeWorkerList.length > 0) &&
                (isActive !== '활성' ||
                  selectedUndoUnsubscribeWorkerList.length > 0))
          }
    />
  );
};

export default WorkerManageCheckBox;
